import { useId, useState } from 'react'

interface ErrorSection {
  label: string | null
  value: string
}

export interface SplitError {
  summary: string
  sections: ErrorSection[]
}

/** Longest first sentence we will promote to a summary when the message has no line breaks. */
const MAX_SENTENCE_SUMMARY = 200

/**
 * Splits an error message into a one-line summary and labelled detail sections.
 * Multi-line messages use the first line as the summary; each following line
 * becomes a section, labelled when it looks like `Label: value`. Single-line
 * messages are split after their first sentence.
 */
export function splitErrorMessage(message: string): SplitError {
  const trimmed = message.trim()
  const newline = trimmed.indexOf('\n')
  let summary: string
  let rest: string

  if (newline >= 0) {
    summary = trimmed.slice(0, newline).trim()
    rest = trimmed.slice(newline + 1)
  } else {
    const sentenceEnd = trimmed.indexOf('. ')
    if (sentenceEnd < 0 || sentenceEnd > MAX_SENTENCE_SUMMARY) return { summary: trimmed, sections: [] }
    summary = trimmed.slice(0, sentenceEnd + 1)
    rest = trimmed.slice(sentenceEnd + 2)
  }

  const sections = rest
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const match = /^([A-Z][\w ]{0,40}):\s+([\s\S]*)$/.exec(line)
      return match ? { label: match[1], value: match[2] } : { label: null, value: line }
    })

  return { summary, sections }
}

interface GccErrorDetailsProps {
  message: string
}

/**
 * Renders an error summary; when the message carries more detail, hovering or
 * focusing the summary reveals a formatted tooltip with the full message and a
 * copy button. The tooltip stays open while hovered so its text can be scrolled
 * and selected.
 */
export function GccErrorDetails({ message }: GccErrorDetailsProps) {
  const { summary, sections } = splitErrorMessage(message)
  const [copied, setCopied] = useState(false)
  const tooltipId = useId()

  if (sections.length === 0) {
    return <p className="text-sm text-red-500 max-w-md mx-auto">{summary}</p>
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      /* clipboard unavailable — the text is still selectable */
    }
  }

  return (
    <div className="group relative inline-block max-w-md mx-auto">
      <p
        tabIndex={0}
        aria-describedby={tooltipId}
        className="text-sm text-red-500 cursor-help underline decoration-dotted decoration-red-300 underline-offset-4 outline-none focus-visible:ring-2 focus-visible:ring-red-200 rounded"
      >
        {summary}
        <span className="ml-1.5 inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600 no-underline align-middle">
          Details
        </span>
      </p>

      {/* pt-2 bridges the gap so moving the pointer into the tooltip keeps it open */}
      <div className="absolute left-1/2 top-full z-50 hidden -translate-x-1/2 pt-2 group-hover:block group-focus-within:block">
        <div
          id={tooltipId}
          role="tooltip"
          className="relative w-[36rem] max-w-[90vw] rounded-xl bg-gray-900 p-3 text-left shadow-2xl ring-1 ring-black/10"
        >
          <span
            aria-hidden="true"
            className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-900"
          />
          <div className="mb-2 flex items-center justify-between gap-3">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Error details</span>
            <button
              type="button"
              onClick={copy}
              className="rounded-md bg-gray-800 px-2 py-0.5 text-[11px] font-medium text-gray-200 hover:bg-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-500"
            >
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
          </div>
          <p className="text-xs font-medium text-gray-100">{summary}</p>
          <div className="mt-2 max-h-72 space-y-2 overflow-auto pr-1">
            {sections.map((section, i) => (
              <div key={i}>
                {section.label && (
                  <div className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {section.label}
                  </div>
                )}
                <pre className="whitespace-pre-wrap break-all rounded-md bg-gray-800 px-2 py-1.5 font-mono text-[11px] leading-relaxed text-gray-200">
                  {section.value}
                </pre>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
